<template>
	<div v-if="show" class="group-info" :style="{ left: pos.x + 'px', top: pos.y + 'px' }" @click.stop>
		<div class="group-card">
			<div class="group-header">
				<div class="avatar">
					<head-image
						variant="group"
						:name="groupDisplayName"
						:url="group.headImageThumb"
						:size="56"
						radius="10%"
						@click.native="showFullImage()"></head-image>
				</div>
				<div class="group-main">
					<div class="title-row">
						<span class="group-name">{{ groupDisplayName }}</span>
						<span v-if="group.isBanned" class="meta-chip danger">
							<i class="el-icon-warning-outline"></i>
							<span>封禁</span>
						</span>
						<span v-if="statusText" class="meta-chip">
							<i :class="statusIcon"></i>
							<span>{{ statusText }}</span>
						</span>
					</div>
					<div class="stats-row">
						<div class="meta-chip" :title="ownerDisplayName">
							<i class="el-icon-user-solid"></i>
							<span class="meta-text">{{ ownerDisplayName }}</span>
						</div>
						<div class="meta-chip">
							<i class="el-icon-user"></i>
							<span>{{ memberSize }}人</span>
						</div>
					</div>
				</div>
			</div>

			<div class="member-strip" :title="`成员 ${memberSize} 人`">
				<template v-if="previewMembers.length">
					<div v-for="member in previewMembers" :key="member.userId" class="member-avatar" :title="resolveMemberName(member)">
						<head-image :name="resolveMemberName(member)" :url="member.headImageThumb" :size="28" radius="50%"></head-image>
					</div>
					<div v-if="memberSize > maxPreviewCount" class="member-avatar member-avatar--more">
						+{{ memberSize - maxPreviewCount }}
					</div>
				</template>
				<div v-else class="member-strip__text">{{ memberStripText }}</div>
			</div>

			<div class="group-action">
				<el-button
					v-if="isInGroup"
					type="primary"
					size="mini"
					class="action-btn"
					icon="el-icon-position"
					@click="onSendMessage()">
					发消息
				</el-button>
				<el-button
					v-else
					type="primary"
					plain
					size="mini"
					class="action-btn"
					icon="el-icon-plus"
					:loading="isJoining"
					:disabled="isJoining || !group.id"
					@click="onJoin()">
					申请加入
				</el-button>
			</div>
		</div>
	</div>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';
import { resolveGroupChatDisplayName } from '../../utils/identityDisplay.js';

export default {
	name: "GroupInfo",
	components: {
		HeadImage
	},
	data() {
		return {
			show: false,
			group: {},
			token: {},
			groupMembers: [],
			isLoadingMembers: false,
			memberLoadFailed: false,
			isJoining: false,
			viewportGap: 10,
			pos: {
				x: 0,
				y: 0
			}
		}
	},
	mounted() {
		window.addEventListener('resize', this.onViewportChange);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.onViewportChange);
	},
	methods: {
		open(group, token, pos) {
			this.show = true;
			this.group = group || {};
			this.token = token || {};
			this.groupMembers = [];
			this.isLoadingMembers = false;
			this.memberLoadFailed = false;
			this.isJoining = false;
			this.pos.x = pos.x;
			this.pos.y = pos.y;
			this.$nextTick(() => {
				this.adjustPos();
			});
			this.loadGroupMembers();
		},
		close() {
			this.show = false;
		},
		onViewportChange() {
			if (!this.show) {
				return;
			}
			this.adjustPos();
		},
		adjustPos() {
			const panel = this.$el;
			if (!panel) {
				return;
			}
			const rect = panel.getBoundingClientRect();
			const maxY = window.innerHeight - rect.height - this.viewportGap;
			const maxX = window.innerWidth - rect.width - this.viewportGap;
			this.pos.y = Math.max(this.viewportGap, Math.min(this.pos.y, maxY));
			this.pos.x = Math.max(this.viewportGap, Math.min(this.pos.x, maxX));
		},
		onSendMessage() {
			let chat = {
				type: 'GROUP',
				targetId: this.group.id,
				showName: this.groupDisplayName,
				headImage: this.group.headImageThumb
			};
			this.chatStore.openChat(chat);
			this.chatStore.setActiveChat(chat);
			if (this.$route.path != "/home/chat") {
				this.$router.push("/home/chat");
			}
			this.show = false;
		},
		onJoin() {
			if (this.isJoining || !this.group.id) {
				return;
			}
			const data = {
				groupId: this.group.id,
				token: this.token
			}
			this.isJoining = true;
			this.$http({
				url: "/group/join",
				method: 'post',
				data: data
			}).then(() => {
				this.groupStore.addGroup(this.group);
				this.$message.success(`您加入了群聊'${this.group.name}'`)
			}).catch((error) => {
				this.$message.error(error && error.message ? error.message : '加入群聊失败');
			}).finally(() => {
				this.isJoining = false;
			})
		},
		showFullImage() {
			if (this.group.headImage) {
				this.$eventBus.$emit("openFullImage", {
					url: this.group.headImage,
					strict: true
				});
			}
		},
		loadGroupMembers() {
			if (!this.group.id) {
				this.memberLoadFailed = true;
				return;
			}
			this.memberLoadFailed = false;
			if (this.isInGroup) {
				this.groupStore.refreshMember(this.group.id, {
					refreshOnline: false,
					pageLimit: 30
				}).catch(() => {
					this.memberLoadFailed = true;
				});
				return;
			}
			this.isLoadingMembers = true;
			this.groupStore.fetchGroupMemberSyncPage(this.group.id, 0, {
				limit: 30,
				maxPages: 1
			}).then((page) => {
				this.groupMembers = Array.isArray(page && page.items) ? page.items : [];
				this.$nextTick(() => {
					this.adjustPos();
				});
			}).catch(() => {
				this.groupMembers = [];
				this.memberLoadFailed = true;
			}).finally(() => {
				this.isLoadingMembers = false;
			});
		},
		resolveMemberName(member) {
			return member && (member.showNickName || member.nickName || member.userName) || '群成员';
		}
	},
	computed: {
		isInGroup() {
			return this.groupStore.isGroup(this.group.id);
		},
		storeGroup() {
			return this.groupStore.findGroup(this.group.id) || null;
		},
		resolvedGroupMembers() {
			if (this.isInGroup && this.storeGroup) {
				return this.storeGroup.members || [];
			}
			return this.groupMembers;
		},
		isResolvedMembersLoading() {
			if (this.isInGroup && this.storeGroup) {
				return this.groupStore.isMembersLoading(this.group.id);
			}
			return this.isLoadingMembers;
		},
		isResolvedMemberLoadFailed() {
			if (this.isInGroup && this.storeGroup) {
				return this.groupStore.isMembersLoadError(this.group.id) && this.resolvedGroupMembers.length === 0;
			}
			return this.memberLoadFailed;
		},
		groupDisplayName() {
			return resolveGroupChatDisplayName(this.group) || this.group.name || '当前群聊';
		},
		ownerName() {
			let member = this.resolvedGroupMembers.find((item) => item.userId == this.group.ownerId);
			return member && this.resolveMemberName(member);
		},
		ownerDisplayName() {
			return this.ownerName || this.group.ownerName || '群主';
		},
		memberSize() {
			const activeMembers = this.resolvedGroupMembers.filter((member) => !member.quit);
			if (activeMembers.length) {
				return activeMembers.length;
			}
			return this.group.memberSize || this.group.memberCount || 0;
		},
		maxPreviewCount() {
			return 5;
		},
		previewMembers() {
			const activeMembers = this.resolvedGroupMembers.filter((member) => !member.quit);
			return activeMembers.slice(0, this.maxPreviewCount);
		},
		memberStripText() {
			if (this.isResolvedMembersLoading) {
				return '成员信息同步中';
			}
			if (this.isResolvedMemberLoadFailed) {
				return '成员信息暂时不可用';
			}
			return '暂无成员信息';
		},
		statusText() {
			if (this.isJoining) {
				return '申请加入中';
			}
			if (this.isResolvedMembersLoading) {
				return '加载成员中';
			}
			if (this.isResolvedMemberLoadFailed) {
				return '成员加载失败';
			}
			return '';
		},
		statusIcon() {
			return this.isJoining || this.isLoadingMembers ? 'el-icon-loading' : 'el-icon-warning-outline';
		}
	}
}
</script>

<style lang="scss" scoped>
.group-info {
	position: fixed;
	width: min(296px, calc(100vw - 20px));
	background: var(--im-card-bg-90, rgba(255, 255, 255, 0.96));
	backdrop-filter: blur(18px);
	border-radius: 12px;
	box-shadow: 0 18px 36px -28px rgba(var(--im-black-rgb), 0.4);
	border: 1px solid var(--im-surface-panel-border);
	z-index: 9999;
	overflow: hidden;

	.group-card {
		padding: 14px;
	}

	.group-header {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.avatar {
		flex-shrink: 0;
	}

	.group-main {
		flex: 1;
		min-width: 0;
	}

	.title-row {
		display: flex;
		align-items: center;
		gap: 6px;
		flex-wrap: wrap;
	}

	.group-name {
		flex: 1;
		min-width: 0;
		font-size: 16px;
		font-weight: 700;
		line-height: 1.3;
		color: var(--im-text-color-primary);
		word-break: break-word;
	}

	.stats-row {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 6px;
		margin-top: 8px;
	}

	.meta-chip {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		min-width: 0;
		max-width: 100%;
		padding: 4px 8px;
		border-radius: 999px;
		background: var(--im-control-tag-neutral-bg);
		color: var(--im-text-color-light);
		font-size: 11px;
		line-height: 1;

		i {
			font-size: 12px;
		}

		.meta-text {
			min-width: 0;
			max-width: 140px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		&.danger {
			background: rgba(var(--im-danger-rgb, 245, 108, 108), 0.1);
			color: var(--im-color-danger);
		}
	}

	.member-strip {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		margin-top: 12px;
		padding-top: 12px;
		border-top: 1px solid var(--im-surface-panel-border);

		&__text {
			font-size: 12px;
			line-height: 1.5;
			color: var(--im-text-color-light);
		}
	}

	.member-avatar {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		margin-left: -8px;
		border-radius: 50%;
		border: 2px solid rgba(var(--im-white-rgb), 0.94);
		background: var(--im-background);
		color: var(--im-text-color-light);
		font-size: 10px;
		font-weight: 600;

		&:first-child {
			margin-left: 0;
		}

		&--more {
			background: var(--im-control-soft-bg-hover);
		}
	}

	.group-action {
		margin-top: 14px;
	}

	.action-btn {
		width: 100%;
		height: 36px;
		border-radius: 8px;
		font-weight: 600;
	}
}

@media (max-width: 640px) {
	.group-info {
		width: calc(100vw - 16px);

		.group-card {
			padding: 12px;
		}

		.group-name {
			font-size: 15px;
		}
	}
}
</style>
